import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, CheckCircle2, Loader2, Sparkles, Bell, ExternalLink, ArrowRight, Radio, BookOpen } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../utils/api";
import PulseAdvisoryModal from "./PulseAdvisoryModal";

const CATEGORIES = ["Income Tax", "GST", "Stock Markets", "Case Laws", "Companies Act", "FEMA/RBI", "Capital Markets", "Labour Law"];

const CAT_COLORS = {
  "Income Tax": "from-emerald-500/20 to-emerald-500/5 border-emerald-500/40 text-emerald-300",
  GST: "from-blue-500/20 to-blue-500/5 border-blue-500/40 text-blue-300",
  "Stock Markets": "from-orange-500/20 to-orange-500/5 border-orange-500/40 text-orange-300",
  "Case Laws": "from-purple-500/20 to-purple-500/5 border-purple-500/40 text-purple-300",
  "Companies Act": "from-violet-500/20 to-violet-500/5 border-violet-500/40 text-violet-300",
  "FEMA/RBI": "from-amber-500/20 to-amber-500/5 border-amber-500/40 text-amber-300",
  "Capital Markets": "from-rose-500/20 to-rose-500/5 border-rose-500/40 text-rose-300",
  "Labour Law": "from-cyan-500/20 to-cyan-500/5 border-cyan-500/40 text-cyan-300",
  Mixed: "from-slate-500/20 to-slate-500/5 border-slate-500/40 text-slate-300",
};

const CAT_DOT = {
  "Income Tax": "bg-emerald-400",
  GST: "bg-blue-400",
  "Stock Markets": "bg-orange-400",
  "Case Laws": "bg-purple-400",
  "Companies Act": "bg-violet-400",
  "FEMA/RBI": "bg-amber-400",
  "Capital Markets": "bg-rose-400",
  "Labour Law": "bg-cyan-400",
  Mixed: "bg-slate-400",
};

export default function NewsletterSubscribe() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [latest, setLatest] = useState([]);
  const [latestLoading, setLatestLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [pulseBlogs, setPulseBlogs] = useState([]);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [activeBlog, setActiveBlog] = useState(null);

  // Fetch latest notifications on mount + auto-refresh every 5 min
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/newsletter/latest?limit=40`);
        if (mounted) {
          setLatest(res.data?.stories || []);
          setLastFetched(new Date());
        }
      } catch (_) {
        // silent fail — keep existing
      } finally {
        if (mounted) setLatestLoading(false);
      }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000); // 5 minutes
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Fetch TaxSetu AI branded advisories (Regulatory Pulse) — same feed that
  // powers the top news ticker. Refresh every 5 minutes.
  useEffect(() => {
    let mounted = true;
    const loadPulse = async () => {
      try {
        const res = await axios.get(`${API_URL}/pulse/latest?limit=9`);
        if (mounted) setPulseBlogs(res.data?.posts || []);
      } catch (_) {
        /* silent */
      } finally {
        if (mounted) setPulseLoading(false);
      }
    };
    loadPulse();
    // Poll every 20 s so a fresh deploy fills in blogs quickly; slows to 5 min once posts arrive.
    const id = setInterval(loadPulse, 20 * 1000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Human-readable "X ago" timer (re-renders every 30s)
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const timeAgo = (() => {
    if (!lastFetched) return "";
    const sec = Math.floor((Date.now() - lastFetched.getTime()) / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    return `${hr}h ago`;
  })();

  const toggleCategory = (cat) => {
    setSelectedCats((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/newsletter/subscribe`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        preferences: selectedCats.length ? selectedCats : undefined,
      });
      if (res.data?.success) {
        setSubscribed(true);
        toast.success(res.data.message || "Subscribed successfully!");
      } else {
        toast.error("Subscription failed. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      className="relative py-16 sm:py-20 px-6 lg:px-12 overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 scroll-mt-24"
      data-testid="newsletter-section"
    >
      {/* Decorative background orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.05),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs text-cyan-300 font-semibold tracking-widest uppercase">
              Daily AI-Curated Brief
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight">
            Stay <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">ahead of every</span><br className="hidden sm:inline"/>
            tax & compliance update
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            AI picks the top <span className="text-emerald-300 font-medium">Income Tax</span>, <span className="text-blue-300 font-medium">GST</span>, <span className="text-orange-300 font-medium">Stock Markets</span>, <span className="text-purple-300 font-medium">Case Laws</span> &amp; corporate-law stories <span className="text-cyan-300">from official Govt portals + leading financial publications</span> — delivered to your inbox at <span className="text-cyan-300 font-medium">9:00 AM IST</span> daily.
          </p>
        </div>

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

          {/* LEFT: Subscribe form (5 cols) */}
          <div className="lg:col-span-5">
            <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl border border-slate-700/60 rounded-3xl p-6 sm:p-7 shadow-2xl shadow-cyan-500/5 h-full">
              {/* Top decorative gradient bar */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />

              {subscribed ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center ring-4 ring-emerald-500/10">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">You're in!</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Your first daily brief lands<br />
                    <span className="text-cyan-300 font-medium">tomorrow at 9:00 AM IST.</span>
                  </p>
                  <div className="mt-6 px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700/50 text-xs text-slate-400">
                    Check your inbox &mdash; or spam folder &mdash; for a welcome ping from{" "}
                    <span className="text-cyan-300 font-mono">updates@taxsetu.ai</span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-4" data-testid="newsletter-form">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white">Subscribe — it's free</h3>
                      <p className="text-[11px] text-slate-500">No spam. One-click unsubscribe.</p>
                    </div>
                  </div>

                  <Input
                    type="text"
                    required
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 h-11"
                    data-testid="newsletter-name-input"
                  />
                  <Input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 h-11"
                    data-testid="newsletter-email-input"
                  />

                  <div className="pt-1">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                      Pick topics <span className="normal-case lowercase">(optional — defaults to all)</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {CATEGORIES.map((cat) => (
                        <button
                          type="button"
                          key={cat}
                          onClick={() => toggleCategory(cat)}
                          className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium border transition-all ${
                            selectedCats.includes(cat)
                              ? "bg-cyan-500/25 border-cyan-400/70 text-cyan-200 shadow-sm shadow-cyan-500/20"
                              : "bg-slate-950/40 border-slate-700/70 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                          }`}
                          data-testid={`newsletter-cat-${cat.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 transition-all"
                    data-testid="newsletter-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subscribing…
                      </>
                    ) : (
                      <>
                        Subscribe — Free <ArrowRight className="w-4 h-4 ml-1.5" />
                      </>
                    )}
                  </Button>

                  <div className="flex flex-wrap gap-3 pt-2 text-[10.5px] text-slate-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Free forever
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Unsubscribe anytime
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" /> 9:00 AM IST
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* RIGHT: Latest Notifications live feed (7 cols) */}
          <div className="lg:col-span-7">
            <div className="relative bg-gradient-to-br from-slate-800/60 to-slate-900/80 backdrop-blur-xl border border-slate-700/60 rounded-3xl p-6 sm:p-7 h-full">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Bell className="w-5 h-5 text-cyan-400" />
                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    Latest Notifications
                  </h3>
                  <span className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    Live
                  </span>
                </div>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider" data-testid="latest-feed-timestamp">
                  {lastFetched ? `Updated ${timeAgo}` : "Auto-updated"}
                </span>
              </div>

              {latestLoading ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-20 rounded-xl bg-slate-800/40 animate-pulse"
                    />
                  ))}
                </div>
              ) : latest.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-500">
                  No notifications fetched yet. Check back in a few minutes.
                </div>
              ) : (
                <>
                  {/* Filter Ribbon — horizontally scrollable category tabs */}
                  {(() => {
                    const counts = latest.reduce((acc, s) => {
                      const k = s.category || "Mixed";
                      acc[k] = (acc[k] || 0) + 1;
                      return acc;
                    }, {});
                    const tabs = ["All", ...CATEGORIES.filter((c) => counts[c])];
                    return (
                      <div
                        className="flex gap-1.5 overflow-x-auto pb-2 mb-3 -mx-1 px-1 custom-scrollbar"
                        data-testid="latest-filter-ribbon"
                      >
                        {tabs.map((tab) => {
                          const isActive = activeFilter === tab;
                          const dot = tab === "All" ? "bg-cyan-400" : CAT_DOT[tab] || CAT_DOT.Mixed;
                          const count = tab === "All" ? latest.length : counts[tab] || 0;
                          return (
                            <button
                              key={tab}
                              type="button"
                              onClick={() => setActiveFilter(tab)}
                              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium uppercase tracking-wider whitespace-nowrap transition-all border ${
                                isActive
                                  ? "bg-cyan-500/20 border-cyan-500/60 text-cyan-200 shadow shadow-cyan-500/10"
                                  : "bg-slate-900/50 border-slate-700/60 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                              }`}
                              data-testid={`latest-filter-${tab.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                              <span>{tab}</span>
                              <span className={`text-[10px] ${isActive ? "text-cyan-300" : "text-slate-500"}`}>{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <div
                    className="space-y-3 max-h-[420px] overflow-y-auto pr-2 -mr-2 custom-scrollbar"
                    data-testid="latest-feed"
                  >
                    {latest
                      .filter((s) => activeFilter === "All" || (s.category || "Mixed") === activeFilter)
                      .map((s, i) => {
                        const cat = s.category || "Mixed";
                        const colorClass = CAT_COLORS[cat] || CAT_COLORS.Mixed;
                        const dotClass = CAT_DOT[cat] || CAT_DOT.Mixed;
                        return (
                          <a
                            key={i}
                            href={s.url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block group relative rounded-xl border bg-gradient-to-r ${colorClass} px-4 py-3 transition-all hover:translate-x-0.5 hover:shadow-lg hover:shadow-cyan-500/5`}
                            data-testid={`latest-item-${i}`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${dotClass}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
                                  <span>{cat}</span>
                                  <span className="text-slate-500">·</span>
                                  <span className="text-slate-400 normal-case">{s.source}</span>
                                </div>
                                <h4 className="text-sm sm:text-[15px] font-semibold text-white leading-snug group-hover:text-cyan-200 transition-colors line-clamp-2">
                                  {s.headline}
                                </h4>
                                {s.summary && (
                                  <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                                    {s.summary}
                                  </p>
                                )}
                              </div>
                              <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-300 flex-shrink-0 mt-1" />
                            </div>
                          </a>
                        );
                      })}
                  </div>
                </>
              )}
              <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
                <span>Sources: IT Dept · CBIC · RBI · SEBI · PIB · SC/HC · ITAT · NCLT · MC · ET · LiveMint</span>
                <span className="text-cyan-400 font-medium">
                  Subscribe to get this in your inbox →
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── TAXSETU AI BLOGS — original in-house informational posts on regulatory updates ── */}
        <div className="mt-12 sm:mt-16" data-testid="taxsetu-blogs-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
                </span>
                <Radio className="w-3.5 h-3.5 text-rose-300" />
                <span className="text-[10.5px] font-bold tracking-widest text-rose-300 uppercase">
                  TaxSetu AI Blog · Live
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                Latest from the TaxSetu AI blog
              </h3>
            </div>
            {pulseBlogs.length > 0 && (
              <button
                onClick={() => setActiveBlog(pulseBlogs[0])}
                className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-cyan-300 hover:text-cyan-200 whitespace-nowrap"
                data-testid="blogs-open-latest-btn"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Read the latest
              </button>
            )}
          </div>

          {pulseLoading && pulseBlogs.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-40 rounded-2xl bg-slate-800/40 animate-pulse" />
              ))}
            </div>
          ) : pulseBlogs.length === 0 ? (
            <div className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-6 text-center text-sm text-slate-500">
              New posts appear here every couple of hours.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pulseBlogs.map((p, i) => {
                const catCls = CAT_COLORS[p.category] || CAT_COLORS.Mixed;
                const dotCls = CAT_DOT[p.category] || CAT_DOT.Mixed;
                const cleanTitle = (p.title || "")
                  .replace(/^TaxSetu AI Alert:\s*/i, "")
                  .replace(/^TaxSetu Insight:\s*/i, "");
                return (
                  <button
                    key={p.slug}
                    onClick={() => setActiveBlog(p)}
                    data-testid={`blog-card-${i}`}
                    className={`group text-left rounded-2xl border bg-gradient-to-br ${catCls} p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-cyan-500/5 relative overflow-hidden`}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
                      <span className="text-[10px] font-bold uppercase tracking-widest">
                        {p.category || "Mixed"}
                      </span>
                      {p.status && p.status !== "Reported" && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border border-white/15 text-slate-300 bg-white/[0.04]">
                          {p.status}
                        </span>
                      )}
                      <span className="ml-auto text-[10px] text-slate-500 font-mono uppercase">
                        {p.published_at
                          ? new Date(p.published_at).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                            })
                          : ""}
                      </span>
                    </div>
                    <h4 className="text-[15px] font-bold text-white leading-snug mb-2 line-clamp-3 group-hover:text-cyan-200 transition-colors">
                      {cleanTitle}
                    </h4>
                    {p.teaser && (
                      <p className="text-[12px] text-slate-300/80 leading-relaxed line-clamp-3">
                        {p.teaser}
                      </p>
                    )}
                    <div className="mt-4 flex items-center gap-1.5 text-[11.5px] font-semibold text-cyan-300">
                      Read blog
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PulseAdvisoryModal
        post={activeBlog}
        isOpen={!!activeBlog}
        onClose={() => setActiveBlog(null)}
        onOpenPermalink={(slug) => {
          setActiveBlog(null);
          navigate(`/pulse/${slug}`);
        }}
      />

      {/* Inline scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(100,116,139,0.4); border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.6); }
      `}</style>
    </section>
  );
}
